import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useDataBus } from "@/lib/dataBus";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Trash2, Pencil, ChevronRight, Check, FolderPlus, ClipboardList, ArrowLeft, AlertTriangle, Calendar, UserCheck, RefreshCw, X as XIcon, Search, Users, Sparkles, Star, ShieldCheck, Briefcase, GraduationCap, PowerOff, Power } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { WorkflowTag } from "@/components/MultiUserSelect";
import { POSITIONS, POSITION_TO_ROLE, positionRole, positionAllowsMemberType, DEPARTMENTS, DEPT_LABEL, MEMBER_TYPES, POSITION_CLS, daysUntilInternshipEnd, userDepartments, allDisplayPositions, memberTeamNames } from "@/lib/teamCatalog";
import RaiseHiringRequestDialog from "@/components/RaiseHiringRequestDialog";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  content_manager: "Content Manager",
  team_lead: "Team Lead",
  associate: "Associate",
  department_admin: "Department Admin",
};

// Iter 45.62 — role hierarchy used for multi-position RBAC and promotion
// grant-tier checks. Kept in sync with backend `ROLE_RANK` in server.py.
const ROLE_RANK = {
  super_admin: 100,
  admin: 90,
  department_admin: 60,
  content_manager: 50,
  team_lead: 30,
  associate: 10,
};

function allRolesOf(u) {
  if (!u) return [];
  const roles = new Set();
  if (u.role) roles.add(u.role);
  (u.positions || []).forEach((p) => { if (p?.role) roles.add(p.role); });
  return Array.from(roles);
}
function highestRoleOf(u) {
  const rs = allRolesOf(u);
  if (!rs.length) return null;
  return rs.reduce((best, r) => (ROLE_RANK[r] || 0) > (ROLE_RANK[best] || 0) ? r : best, rs[0]);
}
function allDepartmentsOf(u) {
  const d = new Set();
  (u?.departments || []).forEach((x) => x && d.add(x));
  if (u?.department) d.add(u.department);
  (u?.positions || []).forEach((p) => p?.department && d.add(p.department));
  return Array.from(d);
}
function daDeptsOf(u) {
  // Only departments where the user specifically holds `department_admin`.
  // A CM/TL position in another dept must NOT be interpreted as DA scope.
  const d = new Set();
  if (u?.role === "department_admin") {
    (u?.departments || []).forEach((x) => x && d.add(x));
    if (u?.department) d.add(u.department);
  }
  (u?.positions || []).forEach((p) => {
    if (p?.role === "department_admin" && p?.department) d.add(p.department);
  });
  return Array.from(d);
}
function maxGrantableRankOf(u) {
  // Mirror of backend `_max_grantable_rank` (iter 45.62).
  const rs = allRolesOf(u);
  if (rs.includes("super_admin"))      return ROLE_RANK.super_admin;
  if (rs.includes("admin"))            return ROLE_RANK.department_admin;
  if (rs.includes("department_admin")) return ROLE_RANK.content_manager;
  if (rs.includes("content_manager"))  return ROLE_RANK.team_lead;
  return 0;
}
const ROLE_CLS = {
  super_admin: "bg-amber-100 text-amber-800 border-amber-300",
  admin: "bg-purple-100 text-purple-700 border-purple-200",
  content_manager: "bg-purple-100 text-purple-700 border-purple-200",
  team_lead: "bg-blue-100 text-blue-700 border-blue-200",
  associate: "bg-slate-100 text-slate-700 border-slate-200",
  department_admin: "bg-violet-100 text-violet-700 border-violet-200",
};

// Iter 45.15 — People page glass polish. Colored-initial avatars help admins
// scan a long list quickly. Palette matches the Payroll sheet so the same
// person keeps the same tint everywhere in the app.
function stringHash(s) {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h);
}
const AVATAR_PALETTE = [
  "bg-violet-500",
  "bg-indigo-500",
  "bg-sky-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-fuchsia-500",
  "bg-lime-500",
];
function InitialsAvatar({ name, size = 32, photo }) {
  const trimmed = (name || "U").trim();
  const initials = trimmed.split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const bg = AVATAR_PALETTE[stringHash(trimmed) % AVATAR_PALETTE.length];
  if (photo) {
    return (
      <img
        src={photo}
        alt={trimmed}
        className="rounded-full object-cover shrink-0 ring-2 ring-white shadow-sm"
        style={{ height: size, width: size }}
      />
    );
  }
  return (
    <div
      className={`${bg} rounded-full flex items-center justify-center text-white font-semibold shrink-0 shadow-sm ring-2 ring-white`}
      style={{ height: size, width: size, fontSize: Math.round(size * 0.4) }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

function PeopleKPI({ label, value, icon: Icon, tint = "slate", testId, onClick, active }) {
  const p = {
    slate:   { ring: "border-slate-200",   iconBg: "bg-slate-100 text-slate-600",  label: "text-slate-500",   val: "text-slate-900",   activeRing: "ring-slate-400 border-slate-400 bg-slate-50" },
    indigo:  { ring: "border-indigo-200",  iconBg: "bg-indigo-50 text-indigo-600", label: "text-indigo-600",  val: "text-indigo-900",  activeRing: "ring-indigo-400 border-indigo-400 bg-indigo-50/60" },
    purple:  { ring: "border-purple-200",  iconBg: "bg-purple-50 text-purple-600", label: "text-purple-600",  val: "text-purple-900",  activeRing: "ring-purple-400 border-purple-400 bg-purple-50/60" },
    emerald: { ring: "border-emerald-200", iconBg: "bg-emerald-50 text-emerald-700", label: "text-emerald-600", val: "text-emerald-800", activeRing: "ring-emerald-400 border-emerald-400 bg-emerald-50/60" },
    amber:   { ring: "border-amber-200",   iconBg: "bg-amber-50 text-amber-700",   label: "text-amber-600",   val: "text-amber-800",   activeRing: "ring-amber-400 border-amber-400 bg-amber-50/60" },
    rose:    { ring: "border-rose-200",    iconBg: "bg-rose-50 text-rose-700",     label: "text-rose-600",    val: "text-rose-900",    activeRing: "ring-rose-400 border-rose-400 bg-rose-50/60" },
  }[tint] || {};
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={active || undefined}
      data-active={active || undefined}
      data-testid={testId}
      className={`
        text-left w-full border rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm transition-all
        ${active ? `ring-2 ring-offset-1 ${p.activeRing}` : `bg-white ${p.ring}`}
        ${onClick ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer active:translate-y-0" : ""}
      `}
    >
      <div className={`h-9 w-9 rounded-xl ${p.iconBg} flex items-center justify-center shrink-0`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className={`text-[10px] uppercase tracking-[0.14em] font-bold ${p.label}`}>{label}</div>
        <div className={`text-2xl font-bold ${p.val} tabular-nums leading-tight`}>{value}</div>
      </div>
    </Comp>
  );
}

export default function Team() {
  const { user, refresh: refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Delete-confirmation dialog target — replaces window.confirm() which is
  // silently suppressed inside preview iframes.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [hiringOpen, setHiringOpen] = useState(false);
  const [myHiring, setMyHiring] = useState([]);

  const load = async () => {
    const [u, p] = await Promise.all([api.get("/users"), api.get("/projects")]);
    setUsers(u.data);
    setProjects(p.data);
    // Load user's own hiring requests (backend filters non-HR/admin to their own)
    try {
      const { data } = await api.get("/hiring-requests");
      setMyHiring(data || []);
    } catch (e) {
      // Endpoint intentionally optional for some roles; log for observability
      // but don't surface a toast — this widget is best-effort.
      console.debug("[Team] load hiring-requests skipped:", e?.response?.status || e?.message);
    }
  };
  useEffect(() => { load(); }, []);
  // Iter 36.7 Phase 3 — refetch whenever any user or project mutation
  // happens anywhere in the app (sidebar, projects tab, manage-team dialog).
  useDataBus(load, ["users", "projects", "any"]);

  const revokeHiring = async (rid) => {
    if (!confirm("Revoke this hiring request? HR will see it removed too.")) return;
    try {
      await api.delete(`/hiring-requests/${rid}`);
      toast.success("Request revoked");
      setMyHiring((prev) => prev.filter((r) => r.id !== rid));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = (u) => setConfirmDelete(u);

  // Iter 45.23 — soft-delete flow.  Instead of removing a user (which used
  // to unassign every task they touched), admins now toggle `is_active`.
  // Deactivated users can't log in and get force-logged-out on their next
  // request, but every task / project reference stays intact.  A confirm
  // dialog is only shown on DEACTIVATE (destructive-ish); reactivate is
  // a single click.
  const [confirmToggle, setConfirmToggle] = useState(null); // full user object
  // The Switch component fires onCheckedChange(next: boolean). When the user
  // flips OFF we open a confirm dialog (deactivation is the destructive-ish
  // path); when they flip ON we reactivate immediately (non-destructive).
  const openToggle = (u, next) => {
    const wantActive = next === undefined ? (u.is_active === false) : Boolean(next);
    if (wantActive) {
      performToggle(u, true);
    } else {
      setConfirmToggle(u);
    }
  };
  const performToggle = async (u, nextActive) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: nextActive });
      toast.success(nextActive ? `${u.name} reactivated — they can log in again` : `${u.name} deactivated — their data is kept, but they can no longer log in`);
      setConfirmToggle(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success(`${confirmDelete.name} removed`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmDelete(null);
    }
  };

  const canEditRow = (target) => {
    if (!user) return false;
    if (target.id === user.id) return "self";
    // Iter 45.62 — multi-position aware RBAC. A user with the role via ANY
    // position (primary or extras) counts; CM/TL linkages are checked across
    // the full positions[] array, not just the top-level scalars.
    const uRoles = allRolesOf(user);
    const tRoles = allRolesOf(target);
    if (uRoles.includes("super_admin")) {
      return !tRoles.includes("super_admin");
    }
    if (uRoles.includes("admin")) {
      return !(tRoles.includes("super_admin") || tRoles.includes("admin"));
    }
    // Below admin tier — can never touch admin / super_admin.
    if (tRoles.includes("super_admin") || tRoles.includes("admin")) return false;
    if (uRoles.includes("department_admin")) {
      const myDA = daDeptsOf(user);
      const tDepts = allDepartmentsOf(target);
      if (myDA.some((d) => tDepts.includes(d))) {
        if (tRoles.includes("department_admin")) return false; // peer DA
        return true;
      }
      // not in my dept — may still be editable via a CM/TL position below
    }
    if (uRoles.includes("content_manager")) {
      const under = (t) =>
        t.manager_id === user.id ||
        (t.positions || []).some((p) => p && p.manager_id === user.id);
      if ((tRoles.includes("team_lead") || tRoles.includes("associate")) && under(target)) return true;
    }
    if (uRoles.includes("team_lead")) {
      const under = (t) =>
        t.team_lead_id === user.id ||
        (t.positions || []).some((p) => p && p.team_lead_id === user.id);
      if (tRoles.includes("associate") && under(target)) return true;
    }
    return false;
  };

  // Super Admin badge visibility toggle — persisted on the user's own record.
  const isSuperAdmin = user?.role === "super_admin";
  // Iter 45.73 — Salary column is visible to the manager tier (Content
  // Manager and above). Backend `_can_view_hr_fields` scopes it; frontend
  // just mirrors the gate so the column renders only when the server will
  // actually ship the value.
  const showSalary = allRolesOf(user).some((r) => ["super_admin", "admin", "department_admin", "content_manager"].includes(r));
  const [badgeOn, setBadgeOn] = useState(user?.show_super_admin_badge !== false);
  useEffect(() => { setBadgeOn(user?.show_super_admin_badge !== false); }, [user?.show_super_admin_badge]);
  const toggleBadge = async (next) => {
    setBadgeOn(next);
    try {
      await api.patch(`/users/${user.id}`, { show_super_admin_badge: next });
      await refreshUser?.();
      toast.success(next ? "Super Admin badge shown" : "Super Admin badge hidden");
    } catch (e) {
      setBadgeOn(!next);
      toast.error(formatApiError(e));
    }
  };

  // Team-page filters — search + a single unified "View" selector that also
  // powers the clickable KPI cards above the table (iter 45.73).
  const [teamQuery, setTeamQuery] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [viewFilter, setViewFilter] = useState("all"); // all | active | inactive | managers | team_leads | associates | interns
  const filteredUsers = useMemo(() => {
    const q = teamQuery.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !(u.name || "").toLowerCase().includes(q) && !(u.email || "").toLowerCase().includes(q)) return false;
      if (filterDept !== "all" && !(u.departments || [u.department]).includes(filterDept)) return false;
      const roles = allRolesOf(u);
      const active = u.is_active !== false;
      switch (viewFilter) {
        case "active":     if (!active) return false; break;
        case "inactive":   if (active)  return false; break;
        case "managers":
          if (!roles.some((r) => ["content_manager","department_admin","admin","super_admin"].includes(r))) return false;
          break;
        case "team_leads":
          if (!roles.includes("team_lead")) return false;
          break;
        case "associates":
          if (!roles.includes("associate") || u.member_type === "intern") return false;
          break;
        case "interns":
          if (u.member_type !== "intern") return false;
          break;
        default: break; // "all"
      }
      return true;
    });
  }, [users, teamQuery, filterDept, viewFilter]);

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin relative bg-slate-50" data-testid="team-page">
      {/* Iter 45.64 — removed the ambient purple/indigo/fuchsia orb layer +
          all glass blur; the page now matches the flat, clean styling of the
          other dashboard pages. */}

      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-purple-50 border border-purple-200 flex items-center justify-center shadow-sm shrink-0">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-slate-900 leading-tight">People</h1>
              <p className="text-[11px] text-slate-500 mt-0.5">Manage members, roles, and hierarchy</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isSuperAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600 border border-slate-200 rounded-full px-2.5 py-1.5 bg-white shadow-sm" data-testid="super-admin-badge-toggle-wrap">
                <span>Show my Super Admin badge</span>
                <input
                  type="checkbox"
                  checked={badgeOn}
                  onChange={(e) => toggleBadge(e.target.checked)}
                  className="accent-amber-500 h-3.5 w-3.5"
                  data-testid="super-admin-badge-toggle"
                />
              </label>
            )}
            {user && ["super_admin", "admin", "department_admin", "content_manager", "team_lead"].includes(user.role) && (
              <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50 h-9 bg-white" onClick={() => setHiringOpen(true)} data-testid="team-raise-hiring-btn">
                <ClipboardList className="h-4 w-4 mr-1.5" /> Raise Hiring Request
              </Button>
            )}
            <Button onClick={() => setWizardOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white shadow-sm h-9" data-testid="add-user-btn">
              <UserPlus className="h-4 w-4 mr-1.5" /> Add member
            </Button>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-5 relative z-10">
        {/* Expiring-internship banner */}
        <InternshipAlertBanner users={users} currentUser={user} onDone={load} />

        {/* My hiring requests — anyone who has raised any request will see them here */}
        {myHiring.length > 0 && (
          <MyHiringRequestsPanel requests={myHiring} onRevoke={revokeHiring} currentUser={user} />
        )}

        {/* KPI strip — Iter 45.73 — each card is a clickable filter that
            drives the "View" selector below. Mirrors the dropdown so admins
            can slice the roster in one tap. */}
        {(() => {
          const activeUsers = users.filter((x) => x.is_active !== false);
          const total = users.length;
          const activeCount = activeUsers.length;
          const inactive = total - activeCount;
          const managers = users.filter((x) => allRolesOf(x).some((r) => ["content_manager","department_admin","admin","super_admin"].includes(r))).length;
          const tls = users.filter((x) => allRolesOf(x).includes("team_lead")).length;
          const assocs = users.filter((x) => allRolesOf(x).includes("associate") && x.member_type !== "intern").length;
          const interns = users.filter((x) => x.member_type === "intern").length;
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="team-kpi-strip">
              <PeopleKPI label="Total"       value={total}       icon={Users}         tint="indigo"  active={viewFilter === "all"}        onClick={() => setViewFilter("all")}        testId="kpi-total" />
              <PeopleKPI label="Active"      value={activeCount} icon={ShieldCheck}   tint="emerald" active={viewFilter === "active"}     onClick={() => setViewFilter("active")}     testId="kpi-active" />
              <PeopleKPI label="Inactive"    value={inactive}    icon={PowerOff}      tint="slate"   active={viewFilter === "inactive"}   onClick={() => setViewFilter("inactive")}   testId="kpi-inactive" />
              <PeopleKPI label="Managers"    value={managers}    icon={Sparkles}      tint="purple"  active={viewFilter === "managers"}   onClick={() => setViewFilter("managers")}   testId="kpi-managers" />
              <PeopleKPI label="Team Leads"  value={tls}         icon={Star}          tint="indigo"  active={viewFilter === "team_leads"} onClick={() => setViewFilter("team_leads")} testId="kpi-tls" />
              <PeopleKPI label="Associates"  value={assocs}      icon={Briefcase}     tint="rose"    active={viewFilter === "associates"} onClick={() => setViewFilter("associates")} testId="kpi-associates" />
              <PeopleKPI label="Interns"     value={interns}     icon={GraduationCap} tint="amber"   active={viewFilter === "interns"}    onClick={() => setViewFilter("interns")}    testId="kpi-interns" />
            </div>
          );
        })()}

        <Card className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="team-table-card">
          {/* Filter bar — search + Department, Role, Team, Status facets.
              All client-side (fast, small dataset) so no roundtrip on typing. */}
          <div className="px-3 py-3 border-b border-slate-200 bg-white flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={teamQuery}
                onChange={(e) => setTeamQuery(e.target.value)}
                placeholder="Search name or email…"
                className="pl-8 h-8 text-sm"
                data-testid="team-search"
              />
            </div>
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="team-filter-dept"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                <SelectItem value="content">Content</SelectItem>
                <SelectItem value="seo">SEO</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="hr">HR</SelectItem>
              </SelectContent>
            </Select>
            <Select value={viewFilter} onValueChange={setViewFilter}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="team-filter-view"><SelectValue placeholder="View" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active Users</SelectItem>
                <SelectItem value="inactive">Inactive Users</SelectItem>
                <SelectItem value="managers">Managers</SelectItem>
                <SelectItem value="team_leads">Team Leads</SelectItem>
                <SelectItem value="associates">Associates (Full-time)</SelectItem>
                <SelectItem value="interns">Interns</SelectItem>
              </SelectContent>
            </Select>
            {(teamQuery || filterDept !== "all" || viewFilter !== "all") && (
              <button
                onClick={() => { setTeamQuery(""); setFilterDept("all"); setViewFilter("all"); }}
                className="text-[11px] text-slate-500 hover:text-purple-600 underline underline-offset-2"
                data-testid="team-clear-filters"
              >
                Clear filters
              </button>
            )}
            <span className="ml-auto text-[11px] text-slate-500">{filteredUsers.length} of {users.length}</span>
          </div>
          {/* Horizontal-scroll wrapper — keeps all columns readable on
              narrow viewports and matches user's `overflow-x-auto` ask. */}
          <div className="overflow-x-auto" data-testid="team-table-scroll">
          <table className="w-full min-w-[1080px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70 text-left">
                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Name</th>
                <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Email</th>
                <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Roles</th>
                <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Manager</th>
                <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Team Leader</th>
                <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Workflow</th>
                <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Joined</th>
                {showSalary && <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 text-right">Salary</th>}
                <th className="w-28 px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={showSalary ? 9 : 8} className="text-center py-14">
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Search className="h-4 w-4 text-slate-400" />
                      </div>
                      <div className="text-sm font-medium text-slate-600">No members match your filters</div>
                      <button
                        onClick={() => { setTeamQuery(""); setFilterDept("all"); setViewFilter("all"); }}
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                        data-testid="team-empty-clear"
                      >
                        Clear all filters
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {filteredUsers.map((u) => {
                const canEdit = canEditRow(u);
                // Iter 36.9.4 — split "Reports to" into Manager (CM name) +
                // Team Leader (TL name). Applies to every user regardless of
                // role — a TL has a Manager but no Team Leader; an associate
                // has both; a CM has neither; admins/DAs typically have
                // neither. Fallback lookup via the user's positions[] array so
                // multi-role users still resolve their primary chain.
                const primaryPos = (Array.isArray(u.positions) && u.positions[0]) || {
                  manager_id: u.manager_id,
                  team_lead_id: u.team_lead_id,
                };
                const managerName = users.find((x) => x.id === (primaryPos.manager_id || u.manager_id))?.name || "";
                const teamLeaderName = users.find((x) => x.id === (primaryPos.team_lead_id || u.team_lead_id))?.name || "";
                // Super Admins that have toggled off their badge appear as "Admin" to everyone
                // EXCEPT themselves. They can still self-verify by looking at role.
                const hideSuperBadge = u.role === "super_admin" && u.show_super_admin_badge === false && u.id !== user?.id;
                const pos = hideSuperBadge ? "Admin" : (u.position || ROLE_LABEL[u.role] || "—");
                const posCls = POSITION_CLS[pos] || ROLE_CLS[u.role] || "bg-slate-100 text-slate-700 border-slate-200";
                const daysLeft = daysUntilInternshipEnd(u);
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className={`border-b border-slate-100 last:border-0 transition-colors hover:bg-slate-50/80 ${u.is_active === false ? "opacity-60" : ""}`}>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <InitialsAvatar name={u.name} photo={u.avatar_url} size={32} />
                        <div className="min-w-0">
                          <div className="inline-flex items-center gap-1.5 flex-wrap">
                            <span className="text-[13px] text-slate-800 font-semibold">{u.name}</span>
                            <WorkflowTag type={u.workflow_type} />
                            {u.member_type === "intern" && (
                              <span className="inline-block text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-bold">Intern</span>
                            )}
                            {u.is_active === false && (
                              <span
                                data-testid={`inactive-pill-${u.id}`}
                                className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-bold border border-slate-300"
                              >
                                <PowerOff className="h-2.5 w-2.5" /> Deactivated
                              </span>
                            )}
                          </div>
                          {u.employee_id && (
                            <div className="text-[10px] text-slate-400 font-mono tracking-wider">#{u.employee_id}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-xs text-slate-500">{u.email}</td>
                    <td className="px-3 py-3.5 text-xs">
                      {/* Roles column (iter 36.6): one flat list of every
                          (department, position) pair the user holds. No
                          "primary" vs "extra" distinction — every row is
                          rendered identically. */}
                      {(() => {
                        const rows = allDisplayPositions(u);
                        if (rows.length === 0) return <span className="text-slate-300">—</span>;
                        return (
                          <div className="flex flex-col gap-1" data-testid={`user-roles-${u.id}`}>
                            {rows.map((r, i) => (
                              <div key={i} className="inline-flex items-center gap-1.5 group">
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold uppercase tracking-wider whitespace-nowrap">
                                  {DEPT_LABEL[r.department] || r.department || "—"}
                                </span>
                                <span className="text-slate-400 text-xs">→</span>
                                <Badge variant="outline" className={`${POSITION_CLS[r.position] || "bg-slate-50 text-slate-700 border-slate-200"} border text-[10px] py-0 px-1.5 whitespace-nowrap`}>
                                  {r.position || "—"}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3.5 text-xs text-slate-600" data-testid={`user-manager-${u.id}`}>{managerName || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-3.5 text-xs text-slate-600" data-testid={`user-team-leader-${u.id}`}>{teamLeaderName || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-3.5 text-xs text-slate-600">
                      {u.workflow_type === "ai" ? (
                        <span data-testid={`user-workflow-${u.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-purple-200 bg-purple-50 text-purple-700 font-semibold">
                          AI
                        </span>
                      ) : u.workflow_type === "manual" ? (
                        <span data-testid={`user-workflow-${u.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                          Manual
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3.5 text-xs text-slate-600">
                      {u.joining_date || <span className="text-slate-300">—</span>}
                      {u.member_type === "intern" && u.internship_end_date && (
                        <div className={`text-[10px] mt-0.5 ${u.internship_replacement_requested ? "text-emerald-600 font-semibold" : (daysLeft !== null && daysLeft <= 30 ? "text-red-600 font-semibold" : "text-slate-400")}`} data-testid={`intern-pill-${u.id}`}>
                          {u.internship_replacement_requested
                            ? <>Replacement requested · ends {u.internship_end_date}</>
                            : <>Intern ends {u.internship_end_date}{daysLeft !== null ? ` · ${daysLeft}d left` : ""}</>}
                        </div>
                      )}
                    </td>
                    {showSalary && (
                      <td className="px-3 py-3.5 text-right font-medium text-slate-700 whitespace-nowrap tabular-nums" data-testid={`salary-cell-${u.id}`}>
                        {u.salary && Number(u.salary) > 0
                          ? <>₹{Number(u.salary).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</>
                          : <span className="text-slate-300">—</span>}
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {canEdit && canEdit !== "self" && (
                          <button onClick={() => setEditing(u)} className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" data-testid={`edit-user-${u.id}`} title="Edit member">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canEdit && canEdit !== "self" && (
                          <Switch
                            checked={u.is_active !== false}
                            onCheckedChange={(next) => openToggle(u, next)}
                            data-testid={`active-toggle-${u.id}`}
                            title={u.is_active === false
                              ? "Off — user can't log in. Toggle to reactivate."
                              : "On — user can log in. Toggle to deactivate."}
                            className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-300"
                          />
                        )}
                        {canEdit && canEdit !== "self" && (
                          <button
                            onClick={() => setConfirmDelete(u)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`delete-user-${u.id}`}
                            title="Delete member permanently"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </Card>
      </div>

      {wizardOpen && <AddMemberWizard users={users} projects={projects} onClose={() => setWizardOpen(false)} onDone={() => { setWizardOpen(false); load(); }} />}

      <RaiseHiringRequestDialog
        open={hiringOpen}
        onClose={() => setHiringOpen(false)}
        onSaved={load}
        defaultDepartment={userDepartments(user)[0] || "content"}
      />
      {editing && <EditMemberDialog target={editing} users={users} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}

      {/* Iter 45.23 — Deactivation confirmation.  Replaces the old delete
          dialog for the primary UX.  Data is preserved; tasks / projects
          stay assigned; the user just loses login access and their tab is
          force-logged-out on the next request. */}
      <AlertDialog open={!!confirmToggle} onOpenChange={(v) => !v && setConfirmToggle(null)}>
        <AlertDialogContent data-testid="confirm-deactivate-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this member?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmToggle ? (
                <>
                  <strong>{confirmToggle.name}</strong> ({confirmToggle.email}) will lose access to PLUTO immediately —
                  they'll be logged out of any open tabs on their next action. Their profile, historical tasks, project
                  assignments and payroll history all stay intact and you can reactivate them any time.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-deactivate-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmToggle && performToggle(confirmToggle, false)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-deactivate-user-confirm"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard-delete dialog (Iter 45.63 — re-exposed via the per-row trash
          button). Hierarchy permissions are enforced server-side in
          DELETE /users/{id}; the button is gated by canEditRow. */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete team member?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete ? (
                <>
                  <strong>{confirmDelete.name}</strong> ({confirmDelete.email}) will be removed.
                  Any references on tasks / projects will be cleared. This cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-user-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddMemberWizard({ users, projects, onClose, onDone }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  // Iter 36.6 — Team-tab restructure. State is now split cleanly:
  //   `details` holds identity + member_type + workflow.
  //   `positions[]` is the flat multi-role list (source of truth for
  //   department + position + hierarchy pointers). No "primary" concept.
  const [details, setDetails] = useState({
    name: "", email: "", password: "",
    member_type: "associate",
    joining_date: new Date().toISOString().slice(0, 10),
    salary: "",
    workflow_type: "manual",
  });
  const [positions, setPositions] = useState([
    { department: "content", position: user?.role === "team_lead" ? "Writer" : "Content Team Lead", manager_id: "", team_lead_id: "", team_name: "", tl_kind: "content" },
  ]);
  const [createdMember, setCreatedMember] = useState(null);
  const [projectChoice, setProjectChoice] = useState("skip");
  const [newProject, setNewProject] = useState({ name: "", site_url: "" });
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [createdProject, setCreatedProject] = useState(null);
  const [bulkKeywords, setBulkKeywords] = useState("");

  // Iter 45.62 — rank-based grant filter. A caller can only create members
  // whose derived role is ≤ their max grantable rank (SA all · Admin ≤ DA ·
  // DA ≤ CM · CM ≤ TL · pure TL → non-tier positions only · associate none).
  const availablePositions = useMemo(() => {
    const maxGrant = maxGrantableRankOf(user);
    if (maxGrant === 0) {
      // Pure TL / associate — can only create non-tier positions (writers,
      // publishers, editors, HR, etc.). This preserves the existing TL
      // onboarding flow where a TL hires an associate under themselves.
      return POSITIONS.filter((p) => !(p in POSITION_TO_ROLE));
    }
    return POSITIONS.filter((p) => {
      const r = POSITION_TO_ROLE[p] || "associate";
      return (ROLE_RANK[r] || 0) <= maxGrant;
    });
  }, [user]);

  // Auto-attach the acting user to the correct manager/TL slot for the
  // first position when it's a TL or associate role — mirrors the old
  // behaviour so CM's create TLs under themselves by default.
  useEffect(() => {
    setPositions((cur) => cur.map((p, i) => {
      if (i !== 0) return p;
      const role = positionRole(p.position);
      const next = { ...p };
      if (role === "team_lead" && !next.manager_id && user?.role === "content_manager") next.manager_id = user.id;
      if (role === "associate" && !next.team_lead_id && user?.role === "team_lead") {
        next.team_lead_id = user.id;
        next.manager_id = user.manager_id || "";
      }
      return next;
    }));
    // eslint-disable-next-line
  }, [user]);

  const primary = positions[0] || {};
  const derivedRole = positionRole(primary.position);
  const positionAllowsMT = positionAllowsMemberType(primary.position);

  const validStep1 = () => {
    if (!details.name || !details.email || !details.password) return false;
    if (positions.length === 0) return false;
    for (const p of positions) {
      const role = positionRole(p.position);
      if (!p.department || !p.position) return false;
      // Iter 36.9.12 — CM/TL anchors are only required for the Content
      // department. Other departments (SEO / Marketing / HR) don't have a
      // Content Manager, so requiring one blocks legitimate onboarding.
      const isContent = p.department === "content";
      if (isContent && role === "team_lead" && !p.manager_id && !(user?.role === "admin" || user?.role === "super_admin")) return false;
      if (isContent && role === "associate" && !p.team_lead_id) return false;
    }
    return true;
  };

  // Iter 37 — defer /auth/register until the FINAL step so a cancelled
  // wizard leaves NO orphan user behind. This helper is called from
  // step 2 (skip / existing) or step 3 (create-project + tasks) for
  // TL/associate flows, and inline at step 1 for admin/CM/DA/SA flows
  // where step 1 IS the last step.
  const buildRegisterPayload = () => {
    const cleanPositions = positions.map((p) => {
      const out = { department: p.department || "content", position: p.position || "" };
      const role = positionRole(p.position);
      out.role = role;
      if (p.manager_id) out.manager_id = p.manager_id;
      if (p.team_lead_id) out.team_lead_id = p.team_lead_id;
      if (p.team_name && p.team_name.trim()) out.team_name = p.team_name.trim();
      if (p.tl_kind && role === "team_lead") out.tl_kind = p.tl_kind;
      return out;
    });
    const first = cleanPositions[0];
    const payload = {
      name: details.name, email: details.email, password: details.password,
      role: first.role,
      department: first.department,
      departments: Array.from(new Set(cleanPositions.map((p) => p.department))),
      position: first.position,
      positions: cleanPositions,
    };
    if (positionAllowsMT) {
      payload.member_type = details.member_type;
      payload.joining_date = details.joining_date;
    }
    if (first.manager_id) payload.manager_id = first.manager_id;
    if (first.team_lead_id) payload.team_lead_id = first.team_lead_id;
    if ((user?.role === "super_admin" || user?.role === "admin") && details.salary !== "" && details.salary !== null) {
      const n = Number(details.salary);
      if (!Number.isNaN(n) && n >= 0) payload.salary = n;
    }
    if (first.role === "team_lead") {
      if (first.team_name) payload.team_name = first.team_name;
      if (first.tl_kind) payload.tl_kind = first.tl_kind;
    }
    if (["content_manager", "team_lead", "associate"].includes(first.role) && ["ai", "manual"].includes(details.workflow_type)) {
      payload.workflow_type = details.workflow_type;
    }
    return payload;
  };

  const registerMember = async () => {
    // Idempotent — if the wizard called this earlier (shouldn't normally
    // happen but guards against double-clicks), return the same result.
    if (createdMember) return createdMember;
    const { data } = await api.post("/auth/register", buildRegisterPayload());
    setCreatedMember(data);
    return data;
  };

  const submitStep1 = async () => {
    if (!validStep1()) {
      const primaryRole = positionRole(positions[0]?.position);
      if (primaryRole === "associate" && !positions[0]?.team_lead_id) {
        toast.error("Please pick a Team Lead — associates must report to one.");
      } else if (primaryRole === "team_lead" && !positions[0]?.manager_id) {
        toast.error("Please pick a Content Manager for this Team Lead.");
      } else {
        toast.error("Please fill all fields (Name, Email, Password, and at least one Role)");
      }
      return;
    }
    const primaryRole = positionRole(positions[0]?.position);
    // For admin / super_admin / CM / DA — step 1 IS the final step. Register + close.
    const finalAtStep1 = ["content_manager", "admin", "super_admin", "department_admin"].includes(primaryRole);
    if (finalAtStep1) {
      setBusy(true);
      try {
        const data = await registerMember();
        toast.success(`${data.position || data.role} created`);
        onDone();
      } catch (e) { toast.error(formatApiError(e)); }
      setBusy(false);
      return;
    }
    // TL / Associate — advance WITHOUT creating the user. Register will
    // happen at final submit (step 2 skip/existing, or step 3).
    setStep(2);
  };

  // Projects visible for "assign to existing" — scoped by the PENDING
  // positions the wizard has selected (no created user yet).
  const eligibleProjects = useMemo(() => {
    const pos = positions[0] || {};
    const role = positionRole(pos.position);
    const mgrId = pos.manager_id;
    const tlId = pos.team_lead_id;
    if (role === "team_lead") {
      return projects.filter((p) => (p.content_manager_ids || []).includes(mgrId));
    }
    return projects.filter((p) => (p.team_lead_ids || []).includes(tlId)
      || (p.content_manager_ids || []).includes(mgrId));
  }, [positions, projects]);

  const submitStep2 = async () => {
    setBusy(true);
    try {
      if (projectChoice === "skip") {
        // Register NOW (final submit for TL/associate skip flow).
        const member = await registerMember();
        toast.success(`${member.name} added`);
        onDone();
        return;
      }
      if (projectChoice === "create") {
        // Validate name; DEFER project creation to step 3 so a cancelled
        // wizard leaves nothing behind.
        if (!newProject.name) { toast.error("Project name is required"); setBusy(false); return; }
        setStep(3);
        return;
      }
      if (projectChoice === "existing") {
        if (!selectedProjectId) { toast.error("Please select a project"); setBusy(false); return; }
        // Register + patch project atomically at this final step.
        const member = await registerMember();
        if (member.role === "team_lead") {
          const target = projects.find((p) => p.id === selectedProjectId);
          const nextIds = Array.from(new Set([...(target?.team_lead_ids || []), member.id]));
          await api.patch(`/projects/${selectedProjectId}`, { team_lead_ids: nextIds });
        } else if (member.role === "associate") {
          // Route to the correct associate array based on the position title.
          const positionTitle = (positions[0]?.position || "").toLowerCase();
          let field = "writer_ids";
          if (positionTitle.includes("publisher")) field = "publisher_ids";
          else if (positionTitle.includes("editor")) field = "editor_ids";
          const target = projects.find((p) => p.id === selectedProjectId);
          const nextIds = Array.from(new Set([...(target?.[field] || []), member.id]));
          await api.patch(`/projects/${selectedProjectId}`, { [field]: nextIds });
        }
        toast.success(`${member.name} assigned to project`);
        onDone();
        nav(`/projects/${selectedProjectId}`);
        return;
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const submitStep3 = async () => {
    setBusy(true);
    try {
      // Iter 37 — Step 3 = final submit for the create-project flow.
      // Register the member, create the project, then optionally add tasks —
      // all inside one try so any failure surfaces to the user with no
      // partial state (aside from what the register-then-project ordering
      // requires, which is unavoidable without a backend transaction).
      const member = await registerMember();
      const positionTitle = (positions[0]?.position || "").toLowerCase();
      const owner_tl = member.role === "team_lead" ? member.id : member.team_lead_id;
      const projectPayload = {
        name: newProject.name,
        site_url: newProject.site_url || "",
        team_lead_ids: owner_tl ? [owner_tl] : [],
        department: "content",
      };
      if (member.role === "associate") {
        if (positionTitle.includes("publisher")) projectPayload.publisher_ids = [member.id];
        else if (positionTitle.includes("editor")) projectPayload.editor_ids = [member.id];
        else projectPayload.writer_ids = [member.id];
      }
      const { data: project } = await api.post("/projects", projectPayload);
      setCreatedProject(project);

      if (bulkKeywords.trim()) {
        const defaults = { project_id: project.id, raw: bulkKeywords, position: "top" };
        if (member.role === "associate") defaults.writer_id = member.id;
        const { data } = await api.post("/tasks/bulk", defaults);
        toast.success(`${member.name} added • Project created • ${data.created} tasks`);
      } else {
        toast.success(`${member.name} added • Project created`);
      }
      onDone();
      nav(`/projects/${project.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === 1 && "Add team member"}
            {step === 2 && "Assign a project"}
            {step === 3 && "Add initial tasks"}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? "bg-purple-600" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} data-testid="wiz-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} data-testid="wiz-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="text" value={details.password} onChange={(e) => setDetails({ ...details, password: e.target.value })} data-testid="wiz-password" />
            </div>

            {/* Iter 36.6 — Flat multi-role editor. Every row is one
                (department, position) with optional hierarchy pointers.
                No primary/extra distinction. */}
            <div>
              <Label>Position &amp; Department</Label>
              <p className="text-[11px] text-slate-500 mb-1">Pick this member's role for each department they belong to. Any TL/associate role will auto-hook into the chosen CM/team.</p>
              <RolesEditor
                positions={positions}
                setPositions={setPositions}
                users={users}
                currentUser={user}
                allowedPositions={availablePositions}
              />
            </div>

            {positionAllowsMT && (
              <>
                <div>
                  <Label>Member type</Label>
                  <RadioGroup value={details.member_type} onValueChange={(v) => setDetails({ ...details, member_type: v })} className="flex gap-4 mt-1" data-testid="wiz-member-type">
                    {MEMBER_TYPES.map((m) => (
                      <label key={m.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <RadioGroupItem value={m.value} data-testid={`wiz-mt-${m.value}`} /> {m.label}
                      </label>
                    ))}
                  </RadioGroup>
                  {details.member_type === "intern" && (
                    <p className="text-[11px] text-slate-500 mt-1">Standard internship = 3 months (auto-calculated from joining date).</p>
                  )}
                </div>
                <div>
                  <Label>Joining date</Label>
                  <Input type="date" value={details.joining_date} onChange={(e) => setDetails({ ...details, joining_date: e.target.value })} data-testid="wiz-joining-date" />
                </div>
              </>
            )}

            {(user?.role === "super_admin" || user?.role === "admin") && (
              <div>
                <Label>Monthly Salary <span className="text-slate-400 text-xs">(optional · ₹)</span></Label>
                <Input type="number" min="0" step="1" value={details.salary}
                  onChange={(e) => setDetails({ ...details, salary: e.target.value })}
                  placeholder="e.g. 45000"
                  data-testid="wiz-salary" />
                <p className="text-[11px] text-slate-500 mt-1">This can also be updated later from the Payroll page.</p>
              </div>
            )}

            {["content_manager", "team_lead", "associate"].includes(derivedRole) && (
              <div>
                <Label>Workflow</Label>
                <Select value={details.workflow_type || "manual"} onValueChange={(v) => setDetails({ ...details, workflow_type: v })}>
                  <SelectTrigger data-testid="wiz-workflow-type"><SelectValue placeholder="Manual or AI" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="ai">AI-assisted</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Only AI Content Managers show up in the "Push to AI" hand-off dropdown.</p>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              How would you like to onboard <strong>{details.name}</strong>?
            </p>
            <RadioGroup value={projectChoice} onValueChange={setProjectChoice} className="space-y-2" data-testid="wiz-project-choice">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-purple-300 cursor-pointer transition">
                <RadioGroupItem value="create" className="mt-0.5" />
                <div>
                  <div className="flex items-center gap-2 font-medium text-slate-900"><FolderPlus className="h-4 w-4 text-purple-500" /> Create a new project</div>
                  <div className="text-xs text-slate-500 mt-0.5">Set up a project owned by {details.name} and optionally add initial tasks.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-purple-300 cursor-pointer transition">
                <RadioGroupItem value="existing" className="mt-0.5" />
                <div>
                  <div className="flex items-center gap-2 font-medium text-slate-900"><ClipboardList className="h-4 w-4 text-purple-500" /> Assign to an existing project</div>
                  <div className="text-xs text-slate-500 mt-0.5">Pick a project under their reporting manager.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-purple-300 cursor-pointer transition">
                <RadioGroupItem value="skip" className="mt-0.5" />
                <div>
                  <div className="font-medium text-slate-900">Skip for now</div>
                  <div className="text-xs text-slate-500 mt-0.5">You can assign projects later from the People page.</div>
                </div>
              </label>
            </RadioGroup>

            {projectChoice === "create" && (
              <div className="space-y-3 pl-2 border-l-2 border-purple-200">
                <div>
                  <Label>Project name</Label>
                  <Input value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} placeholder="e.g. TechRadar Blog" data-testid="wiz-new-project-name" />
                </div>
                <div>
                  <Label>Site URL (optional)</Label>
                  <Input value={newProject.site_url} onChange={(e) => setNewProject({ ...newProject, site_url: e.target.value })} placeholder="https://…" />
                </div>
              </div>
            )}
            {projectChoice === "existing" && (
              <div className="pl-2 border-l-2 border-purple-200">
                <Label>Project</Label>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger data-testid="wiz-existing-project"><SelectValue placeholder="Select a project" /></SelectTrigger>
                  <SelectContent>
                    {eligibleProjects.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No eligible projects</div>}
                    {eligibleProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {positionRole(positions[0]?.position) === "team_lead" && selectedProjectId && (
                  <p className="text-[11px] text-amber-600 mt-1">Note: This project will be reassigned to {details.name} as its Team Lead.</p>
                )}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Add some initial tasks to <strong>{newProject.name}</strong>? You can skip and add them later.
            </p>
            <div>
              <Label>Keywords (one per line)</Label>
              <Textarea rows={7} value={bulkKeywords} onChange={(e) => setBulkKeywords(e.target.value)}
                className="font-mono text-sm mt-1"
                placeholder={`best budget laptops\nseo tips for beginners\nhow to write a blog post`}
                data-testid="wiz-keywords" />
              {positionRole(positions[0]?.position) === "associate" && bulkKeywords.trim() && (
                <p className="text-[11px] text-purple-600 mt-1">Tasks will be assigned to {details.name} as writer.</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && step !== 3 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {step === 1 && (
            <Button onClick={submitStep1} disabled={busy || !validStep1()} className="bg-purple-600 hover:bg-purple-700" data-testid="wiz-next-1">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button onClick={submitStep2} disabled={busy} className="bg-purple-600 hover:bg-purple-700" data-testid="wiz-next-2">
              {projectChoice === "skip" ? "Finish" : projectChoice === "create" ? <>Create <ChevronRight className="h-4 w-4 ml-1" /></> : "Assign & finish"}
            </Button>
          )}
          {step === 3 && (
            <Button onClick={submitStep3} disabled={busy} className="bg-purple-600 hover:bg-purple-700" data-testid="wiz-next-3">
              <Check className="h-4 w-4 mr-1" /> {bulkKeywords.trim() ? "Add tasks & finish" : "Finish"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtraPositionsPanel({ target, onDone }) {
  // Manage a user's EXTRA positions (positions[1:]) — the primary position at
  // index 0 is edited via the main dialog fields above. Admin-tier only.
  const [positions, setPositions] = useState(target.positions || []);
  const [newRole, setNewRole] = useState("department_admin");
  const [newDept, setNewDept] = useState("seo");
  const [busy, setBusy] = useState(false);
  const roles = ["admin", "department_admin", "content_manager", "team_lead", "associate"];

  const addOne = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${target.id}/positions`, { role: newRole, department: newDept });
      setPositions(data.positions || []);
      toast.success("Position added");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const removeOne = async (idx) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/users/${target.id}/positions/${idx}`);
      setPositions(data.positions || []);
      toast.success("Position removed");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const extras = positions.slice(1);
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="extra-positions-panel">
      <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Extra positions</div>
      <p className="text-[11px] text-slate-500">
        This user may hold additional roles across departments. Primary position is edited above.
      </p>
      {extras.length === 0 ? (
        <div className="text-[11px] text-slate-400 italic">No extra positions yet.</div>
      ) : (
        <div className="space-y-1">
          {extras.map((p, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded bg-white border border-slate-200" data-testid={`extra-pos-row-${i + 1}`}>
              <span className="text-[10px] px-1.5 py-[1px] rounded border border-purple-200 bg-purple-50 text-purple-700 font-semibold uppercase tracking-wider">
                {(p.role || "").replace("_", " ")}
              </span>
              <span className="text-xs text-slate-700">{DEPT_LABEL[p.department] || p.department || "—"}</span>
              <button
                type="button"
                onClick={() => removeOne(i + 1)}
                disabled={busy}
                className="ml-auto text-[10px] text-red-500 hover:text-red-700 disabled:opacity-50"
                data-testid={`extra-pos-remove-${i + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 items-end pt-1">
        <div className="flex-1">
          <Label className="text-[10px]">Role</Label>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger data-testid="extra-pos-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {roles.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-[10px]">Department</Label>
          <Select value={newDept} onValueChange={setNewDept}>
            <SelectTrigger data-testid="extra-pos-dept"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DEPARTMENTS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={addOne}
          disabled={busy}
          className="bg-purple-600 hover:bg-purple-700 h-9"
          data-testid="extra-pos-add"
        >+ Add</Button>
      </div>
    </div>
  );
}

function EditMemberDialog({ target, users, onClose, onDone }) {
  const { user } = useAuth();
  // Iter 36.6 — Multi-role UI. Load the target's positions[] (or synthesize
  // one from the legacy scalar fields when the array is empty) and let the
  // admin edit them as a flat list. When `position` is missing on a stored
  // position we sharpen it from role+tl_kind so the dropdown shows the
  // right value on open.
  const posLabelFromRole = (role, tl_kind) => {
    if (role === "team_lead") return tl_kind === "publishing" ? "Publishing Team Lead" : "Content Team Lead";
    if (role === "content_manager") return "Content Manager";
    if (role === "super_admin") return "Super Admin";
    if (role === "admin") return "Admin";
    if (role === "department_admin") return "Department Admin";
    return "Writer";
  };
  const initialPositions = (Array.isArray(target.positions) && target.positions.length > 0)
    ? target.positions.map((p) => ({
        department: p.department || "content",
        position: p.position || posLabelFromRole(p.role, p.tl_kind),
        role: p.role || "",
        manager_id: p.manager_id || "",
        team_lead_id: p.team_lead_id || "",
        team_name: p.team_name || "",
        tl_kind: p.tl_kind || "",
      }))
    : [{
        department: target.department || "content",
        position: target.position || posLabelFromRole(target.role, target.tl_kind),
        role: target.role || "",
        manager_id: target.manager_id || "",
        team_lead_id: target.team_lead_id || "",
        team_name: target.team_name || "",
        tl_kind: target.tl_kind || "",
      }];
  const [positions, setPositions] = useState(initialPositions);
  const [form, setForm] = useState({
    name: target.name,
    email: target.email || "",
    password: "",
    member_type: target.member_type || "",
    joining_date: target.joining_date || "",
    internship_end_date: target.internship_end_date || "",
    salary: target.salary != null ? String(target.salary) : "",
  });

  const isAdmin = (user?.role === "admin" || user?.role === "super_admin");
  // Iter 45.73 — salary edit is available to CM / DA / Admin / SA. Mirrors
  // backend `_can_view_hr_fields` (which also allows HR dept members).
  const canEditSalary = allRolesOf(user).some((r) => ["super_admin", "admin", "department_admin", "content_manager"].includes(r));
  const primaryRole = positionRole(positions[0]?.position);
  const canEditIntern = positionAllowsMemberType(positions[0]?.position);

  // Iter 45.62 — rank-based availability for the Edit dialog. Mirrors the
  // backend grant-tier check so a CM only sees TL + associate-tier positions,
  // a Content DA sees CM + TL + associate-tier, an Admin sees everything
  // except Super Admin / Admin, and a pure TL sees no promotion options at
  // all (their max grant is 0 — only profile fields are editable).
  const availablePositions = useMemo(() => {
    const maxGrant = maxGrantableRankOf(user);
    return POSITIONS.filter((p) => {
      const r = POSITION_TO_ROLE[p] || "associate";
      return (ROLE_RANK[r] || 0) <= maxGrant;
    });
  }, [user]);
  const canEditPositions = availablePositions.length > 0;

  const submit = async () => {
    const patch = {};
    if (form.name && form.name !== target.name) patch.name = form.name;
    // Iter 36.9.12 — Edit dialog now supports email updates (matching the
    // Add-Member wizard). Backend allows admin-tier to change any email.
    if (form.email && form.email !== (target.email || "")) patch.email = form.email.trim().toLowerCase();
    if (form.password) patch.password = form.password;
    // Positions become the single source of truth. Backend derives role /
    // department / manager_id / team_lead_id / team_name / tl_kind from
    // positions[0] for legacy compat.
    if (canEditPositions) {
      const cleanPositions = positions.map((p) => {
        const role = positionRole(p.position);
        const out = { department: p.department || "content", position: p.position || "", role };
        if (p.manager_id) out.manager_id = p.manager_id;
        if (role === "associate" && p.team_lead_id) out.team_lead_id = p.team_lead_id;
        if (p.team_name && p.team_name.trim()) out.team_name = p.team_name.trim();
        if (p.tl_kind && role === "team_lead") out.tl_kind = p.tl_kind;
        return out;
      });
      patch.positions = cleanPositions;
    }
    if (canEditIntern) {
      if (form.member_type !== (target.member_type || "")) patch.member_type = form.member_type || null;
      if (form.joining_date && form.joining_date !== (target.joining_date || "")) patch.joining_date = form.joining_date;
      if (form.internship_end_date && form.internship_end_date !== (target.internship_end_date || "")) patch.internship_end_date = form.internship_end_date;
    } else if (target.member_type === "intern") {
      // Promoted out of intern/associate roles (e.g. to TL/CM) — clear intern fields
      patch.member_type = null;
      patch.internship_end_date = null;
    }
    // Iter 45.73 — salary edit for CM / DA / Admin / SA
    if (canEditSalary) {
      const trimmed = (form.salary || "").toString().trim();
      if (trimmed === "") {
        if (target.salary != null && Number(target.salary) !== 0) patch.salary = 0;
      } else {
        const n = Number(trimmed);
        if (!Number.isNaN(n) && n >= 0 && n !== Number(target.salary || 0)) patch.salary = n;
      }
    }
    try {
      await api.patch(`/users/${target.id}`, patch);
      toast.success("Member updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit {target.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" />
            </div>
          </div>
          <div>
            <Label>Reset password <span className="text-slate-400 text-xs">(blank = keep)</span></Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="edit-password" />
          </div>

          <div>
            <Label>Position &amp; Department</Label>
            <p className="text-[11px] text-slate-500 mb-1">
              {canEditPositions
                ? "Every role this person holds. No primary — click \"+ More Roles\" to add another."
                : "You cannot change this member's roles — only profile fields are editable."}
            </p>
            {canEditPositions ? (
              <RolesEditor
                positions={positions}
                setPositions={setPositions}
                users={users}
                currentUser={user}
                allowedPositions={availablePositions}
              />
            ) : (
              <div className="text-[11px] text-slate-500 italic p-3 border border-slate-200 rounded bg-slate-50">
                Current:
                {positions.map((p, i) => (
                  <div key={i} className="mt-1 text-slate-700 not-italic">
                    <span className="font-medium">{p.position || "—"}</span>
                    <span className="text-slate-400"> · {p.department || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {canEditIntern && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label>Member type</Label>
                <Select value={form.member_type || "associate"} onValueChange={(v) => setForm({ ...form, member_type: v })}>
                  <SelectTrigger data-testid="edit-member-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{MEMBER_TYPES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Joining date</Label>
                <Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
              </div>
              {form.member_type === "intern" && (
                <div>
                  <Label>Intern ends</Label>
                  <Input type="date" value={form.internship_end_date} onChange={(e) => setForm({ ...form, internship_end_date: e.target.value })} />
                </div>
              )}
            </div>
          )}

          {canEditSalary && (
            <div>
              <Label>Monthly Salary <span className="text-slate-400 text-xs">(₹ · leave blank to clear)</span></Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={form.salary}
                onChange={(e) => setForm({ ...form, salary: e.target.value })}
                placeholder="e.g. 45000"
                data-testid="edit-salary"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Private HR field — only Content Managers and above can see / edit this.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-purple-600 hover:bg-purple-700" data-testid="edit-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Internship expiring banner + actions ----------

function InternshipAlertBanner({ users, currentUser, onDone }) {
  const [openId, setOpenId] = useState(null); // user id whose action panel is open
  // Iter 45.65 — Bug fix: the gate previously excluded super_admin and
  // department_admin, so the primary admin account never saw the banner.
  // Now multi-position aware too — a user holding CM/TL via positions[]
  // (not the top-level role) also sees it.
  const allowedRoles = ["super_admin", "admin", "department_admin", "content_manager", "team_lead"];
  if (!currentUser || !allRolesOf(currentUser).some((r) => allowedRoles.includes(r))) return null;

  // Filter interns visible to this user, ending in <= 30 days (or already
  // past). Iter 36.9.13 — Bug 18: once the caller has requested a
  // replacement (`internship_replacement_requested`), the popup for that
  // user disappears until the interim is resolved by HR.
  const expiring = users
    .map((u) => ({ user: u, days: daysUntilInternshipEnd(u) }))
    .filter((x) => x.days !== null && x.days <= 30 && !x.user.internship_replacement_requested)
    .sort((a, b) => a.days - b.days);

  if (expiring.length === 0) return null;

  return (
    <div className={`shake-animation relative rounded-lg border border-red-300 bg-red-50 px-4 py-3`} data-testid="internship-alert">
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-900">{expiring.length} intern{expiring.length === 1 ? "'s" : "s'"} tenure ends in ≤ 1 month</p>
          <p className="text-xs text-red-700 mt-0.5">Extend the internship, convert to associate, or raise a hiring request with HR.</p>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {expiring.map(({ user: u, days }) => (
          <InternshipAlertRow key={u.id} user={u} days={days} open={openId === u.id} onToggle={() => setOpenId(openId === u.id ? null : u.id)} onDone={onDone} />
        ))}
      </div>
    </div>
  );
}

function InternshipAlertRow({ user, days, open, onToggle, onDone }) {
  const [mode, setMode] = useState(null); // "extend" | "replace" | "convert"
  const [newEnd, setNewEnd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isPast = days <= 0;

  const startMode = (m) => {
    setMode(m);
    if (m === "extend") setNewEnd(user.internship_end_date || new Date().toISOString().slice(0, 10));
    else if (m === "replace") setReason("");
  };
  const cancel = () => { setMode(null); setReason(""); };

  const doExtend = async () => {
    if (!newEnd) { toast.error("Pick a new end date"); return; }
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { internship_end_date: newEnd });
      toast.success(`Internship extended to ${newEnd}`);
      cancel(); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const doConvert = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { member_type: "associate", internship_end_date: null });
      toast.success(`${user.name} converted to Associate`);
      cancel(); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const doReplace = async () => {
    setBusy(true);
    try {
      await api.post("/hiring-requests", {
        department: user.department || "content",
        position: user.position || "Writer",
        reason: reason || `Replacement for intern ${user.name} (ends ${user.internship_end_date})`,
        replacing_user_id: user.id,
      });
      // Iter 36.9.13 — Bug 18: stamp the flag on the user so the expiring
      // popup and the People-row pill both disappear until HR resolves it.
      await api.patch(`/users/${user.id}`, { internship_replacement_requested: true }).catch(() => {});
      toast.success(`Hiring request raised with HR`);
      cancel(); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-md border border-red-200 shake-animation-none">
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-red-50/40 transition" data-testid={`intern-row-${user.id}`}>
        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate">
            {user.name} <span className="text-slate-400 text-xs">· {user.position || "Intern"} · {user.department || "content"}</span>
          </div>
          <div className={`text-[11px] ${isPast ? "text-red-700 font-bold" : "text-red-600"}`}>
            {isPast ? `Ended ${Math.abs(days)}d ago (${user.internship_end_date})` : `Ends in ${days}d (${user.internship_end_date})`}
          </div>
        </div>
        <ChevronRight className={`h-4 w-4 text-slate-400 transition ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-red-100">
          {!mode && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => startMode("extend")} data-testid={`intern-extend-${user.id}`}><Calendar className="h-3.5 w-3.5 mr-1.5" /> Increase duration</Button>
              <Button size="sm" variant="outline" onClick={() => startMode("replace")} data-testid={`intern-replace-${user.id}`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Request replacement</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => startMode("convert")} data-testid={`intern-convert-${user.id}`}><UserCheck className="h-3.5 w-3.5 mr-1.5" /> Convert to Associate</Button>
            </div>
          )}
          {mode === "extend" && (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">New end date</Label>
                <Input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className="mt-1 h-9" data-testid={`intern-extend-date-${user.id}`} />
              </div>
              <Button size="sm" onClick={doExtend} disabled={busy} className="bg-purple-600 hover:bg-purple-700 h-9">{busy ? "Saving…" : "Extend"}</Button>
              <Button size="sm" variant="ghost" onClick={cancel} className="h-9">Cancel</Button>
            </div>
          )}
          {mode === "replace" && (
            <div className="space-y-2">
              <Textarea rows={2} placeholder="Optional note to HR…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`intern-replace-reason-${user.id}`} />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button>
                <Button size="sm" onClick={doReplace} disabled={busy} className="bg-red-600 hover:bg-red-700">{busy ? "Sending…" : "Send to HR"}</Button>
              </div>
            </div>
          )}
          {mode === "convert" && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <p className="text-xs text-emerald-800 flex-1">This will convert <strong>{user.name}</strong> from Intern to Associate and clear the internship end date.</p>
              <Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button>
              <Button size="sm" onClick={doConvert} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">{busy ? "Converting…" : "Yes, convert"}</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- My Hiring Requests panel (Team page) ----------

const HR_STATUS_META = {
  open:        { label: "Open",        cls: "bg-amber-50 text-amber-700 border-amber-200" },
  in_progress: { label: "In Progress", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  filled:      { label: "Filled",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed:      { label: "Closed",      cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function MyHiringRequestsPanel({ requests, onRevoke, currentUser }) {
  // Show only requests where the current user is a requester
  const own = requests.filter((r) => (r.requested_by_ids || []).includes(currentUser?.id));
  if (own.length === 0) return null;
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50/60">
        <ClipboardList className="h-4 w-4 text-rose-500" />
        <span className="font-display text-sm font-semibold text-slate-800">My Hiring Requests</span>
        <span className="text-xs text-slate-500">{own.length}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-white border-b border-slate-100">
          <tr>
            <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Profile</th>
            <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Department</th>
            <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Notes</th>
            <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Raised</th>
            <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
            <th className="w-24 px-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {own.map((r) => {
            const status = r.status || "open";
            const meta = HR_STATUS_META[status] || HR_STATUS_META.open;
            const posCls = POSITION_CLS[r.position] || "bg-slate-100 text-slate-700 border-slate-200";
            const canRevoke = status !== "filled" && status !== "closed";
            return (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`my-hr-row-${r.id}`}>
                <td className="px-4 py-2"><Badge className={`${posCls} border`}>{r.position}</Badge></td>
                <td className="px-4 py-2 text-xs text-slate-600">{DEPT_LABEL[r.department] || r.department}</td>
                <td className="px-4 py-2 text-xs text-slate-600 max-w-[300px] truncate">{r.reason || <span className="text-slate-300 italic">—</span>}</td>
                <td className="px-4 py-2 text-[11px] text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-2"><Badge className={`${meta.cls} border`}>{meta.label}</Badge></td>
                <td className="px-2 py-2 text-right">
                  {canRevoke && (
                    <Button variant="outline" size="sm" onClick={() => onRevoke(r.id)} className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid={`my-hr-revoke-${r.id}`}>
                      <XIcon className="h-3 w-3 mr-1" /> Revoke
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}




/* ================================================================
   RolesEditor — flat positions[] editor used by both AddMemberWizard
   and EditMemberDialog. No "primary" concept: every row is one
   (department, position) tuple with optional hierarchy pointers.
   User can add rows with "+ More Roles" and remove them individually.
   Iter 36.6 — replaces the split Position/Department/Manager/TL fields.
   ================================================================ */

function RolesEditor({ positions, setPositions, users, currentUser, allowedPositions, disabledDept }) {
  const update = (i, patch) => setPositions(positions.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const remove = (i) => setPositions(positions.filter((_, idx) => idx !== i));
  const add = () => setPositions([...positions, { department: "content", position: allowedPositions[0] || "Writer", manager_id: "", team_lead_id: "", team_name: "", tl_kind: "" }]);
  return (
    <div className="space-y-2" data-testid="roles-editor">
      {positions.length === 0 && (
        <div className="text-xs text-slate-400 italic px-2 py-2 border border-dashed border-slate-200 rounded">
          No roles yet. Click "+ More Roles" to add one.
        </div>
      )}
      {positions.map((p, i) => (
        <PositionRow
          key={i}
          position={p}
          onChange={(patch) => update(i, patch)}
          onRemove={positions.length > 1 ? () => remove(i) : null}
          users={users}
          currentUser={currentUser}
          allowedPositions={allowedPositions}
          disabledDept={disabledDept}
          index={i}
        />
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 transition"
        data-testid="roles-add-more"
      >
        <UserPlus className="h-3 w-3" /> More Roles
      </button>
    </div>
  );
}

function PositionRow({ position, onChange, onRemove, users, currentUser, allowedPositions, disabledDept, index }) {
  const derivedRole = positionRole(position.position);
  const isTL = derivedRole === "team_lead";
  const isAssociate = derivedRole === "associate";
  const isCM = derivedRole === "content_manager";
  const tlKind = position.position === "Publishing Team Lead" ? "publishing" : (position.position === "Content Team Lead" ? "content" : (position.tl_kind || ""));

  // Content Managers scoped to this position's department. Include the
  // current user themself so a CM/etc. can pick themselves for the flow.
  const cmsInDept = useMemo(() => {
    return users.filter((u) => {
      const pp = Array.isArray(u.positions) && u.positions.length ? u.positions : [{role: u.role, department: u.department}];
      return pp.some((x) => x.role === "content_manager" && (x.department || "content") === position.department);
    });
  }, [users, position.department]);

  // Teams under the picked CM in this department: unique team_names of TLs
  // whose manager_id === picked CM.
  const teamsUnderCM = useMemo(() => {
    if (!position.manager_id) return [];
    const names = new Set();
    for (const u of users) {
      const pp = Array.isArray(u.positions) && u.positions.length ? u.positions : [{role: u.role, department: u.department, manager_id: u.manager_id, team_name: u.team_name}];
      for (const x of pp) {
        if (x.role === "team_lead" && x.manager_id === position.manager_id && (x.department || "content") === position.department && x.team_name) {
          names.add(x.team_name);
        }
      }
    }
    return Array.from(names);
  }, [users, position.manager_id, position.department]);

  // TLs directly under the picked CM in this department (any tl_kind). Used
  // by the associate variant of the row so the caller picks a Team Lead
  // straight from the CM's pool (iter 36.9). Falls back to no options when
  // no CM is picked yet. Uses BOTH positions[].manager_id AND the user's
  // top-level manager_id — some legacy TLs (like Inzia) have manager_id on
  // the user document but not on their positions[0] entry, so filtering
  // ONLY by the position field would exclude them.
  const tlsUnderCM = useMemo(() => {
    if (!position.manager_id) return [];
    return users.filter((u) => {
      const pp = Array.isArray(u.positions) && u.positions.length ? u.positions : [{role: u.role, department: u.department, manager_id: u.manager_id, team_name: u.team_name, tl_kind: u.tl_kind}];
      // Special case: CM self-picking as their own TL — a user who is BOTH
      // CM and TL should appear when they've been chosen as the manager,
      // even though their own manager_id is null. (e.g. Komal is CM + Content TL.)
      if (u.id === position.manager_id) {
        const hasTLRole = pp.some((x) => x.role === "team_lead" && (x.department || u.department || "content") === position.department);
        if (hasTLRole) return true;
      }
      return pp.some((x) => {
        if (x.role !== "team_lead") return false;
        const dept = x.department || u.department || "content";
        if (dept !== position.department) return false;
        const mgr = x.manager_id || u.manager_id;
        return mgr === position.manager_id;
      });
    });
  }, [users, position.manager_id, position.department]);

  const onPickTeamLead = (tlId) => {
    // Associates now pick a Team Lead directly from the CM's pool. Team
    // names are no longer surfaced in the UI (iter 36.9.1) — we still stamp
    // `team_name` on the payload for backend compat / legacy readers, but
    // it isn't displayed anywhere in this dialog.
    const tl = users.find((u) => u.id === tlId);
    onChange({ team_lead_id: tlId, team_name: tl?.team_name || "" });
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-white" data-testid={`position-row-${index}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Role {index + 1}</div>
        {onRemove && (
          <button type="button" onClick={onRemove} className="text-slate-400 hover:text-red-600 p-1" data-testid={`position-remove-${index}`} title="Remove this role">
            <XIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-slate-600">Department</Label>
          <Select value={position.department || "content"} onValueChange={(v) => onChange({ department: v, manager_id: "", team_lead_id: "", team_name: "" })} disabled={disabledDept}>
            <SelectTrigger className="h-8" data-testid={`position-dept-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {DEPARTMENTS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] text-slate-600">Position</Label>
          <Select
            value={position.position || ""}
            onValueChange={(v) => {
              const role = positionRole(v);
              onChange({
                position: v,
                tl_kind: v === "Publishing Team Lead" ? "publishing" : (v === "Content Team Lead" ? "content" : ""),
                team_lead_id: role === "associate" ? position.team_lead_id : "",
              });
            }}
          >
            <SelectTrigger className="h-8" data-testid={`position-pos-${index}`}><SelectValue placeholder="Pick position" /></SelectTrigger>
            <SelectContent>
              {allowedPositions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Iter 36.9.12 — CM / TL pickers only make sense for the Content
          department. SEO / Marketing / HR positions report to a Department
          Admin (or nobody) and don't have a CM anchor. Hiding this whole
          row-half for non-Content prevents the "Add Member" wizard from
          getting stuck when the target dept has zero CMs. */}
      {(isTL || isAssociate) && position.department === "content" && (
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-100">
          <div>
            <Label className="text-[11px] text-slate-600">{isTL ? "Reports to (CM)" : "Content Manager"}</Label>
            <Select value={position.manager_id || ""} onValueChange={(v) => onChange({ manager_id: v, team_lead_id: "", team_name: isTL ? position.team_name : "" })}>
              <SelectTrigger className="h-8" data-testid={`position-mgr-${index}`}><SelectValue placeholder="Pick CM" /></SelectTrigger>
              <SelectContent>
                {cmsInDept.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No CMs in this department.</div>}
                {cmsInDept.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}{m.id === currentUser?.id ? " (me)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-600">{isTL ? "Team" : "Team Lead"}</Label>
            {isTL ? (
              <TLTeamPicker
                value={position.team_name || ""}
                onChange={(v) => onChange({ team_name: v })}
                teams={teamsUnderCM}
                disabled={!position.manager_id}
                testId={`position-team-name-${index}`}
              />
            ) : (
              <Select value={position.team_lead_id || ""} onValueChange={onPickTeamLead} disabled={!position.manager_id}>
                <SelectTrigger className="h-8" data-testid={`position-tl-${index}`}><SelectValue placeholder={position.manager_id ? "Pick Team Lead" : "Pick CM first"} /></SelectTrigger>
                <SelectContent>
                  {tlsUnderCM.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No Team Leads under this CM yet.</div>}
                  {tlsUnderCM.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                      {t.tl_kind === "publishing" ? " · Publishing TL" : " · Content TL"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}
      {isTL && (
        <p className="text-[10px] text-slate-500 pt-1">
          {tlKind === "publishing" ? "Publishing TL" : "Content TL"} of <strong>{position.team_name || "(no team)"}</strong>. Anyone who joins this team will report to you.
        </p>
      )}
      {isAssociate && position.team_lead_id && (
        <p className="text-[10px] text-slate-500 pt-1">
          Reports to <strong>{(users.find((u) => u.id === position.team_lead_id)?.name) || "—"}</strong>.
        </p>
      )}
      {isCM && (
        <p className="text-[10px] text-slate-500 pt-1">Content Manager of the <strong>{DEPT_LABEL[position.department] || position.department}</strong> department.</p>
      )}
    </div>
  );
}


/* --------- TLTeamPicker ---------
   Team-name field for the TL variant of PositionRow. Behaviour:
   - By default shows a Select populated with team_names that already exist
     under the picked CM (so you can move a TL between teams without typing).
   - A "+ New team…" option at the bottom flips the field to an inline
     text Input so an admin can name a brand-new team.
   - If the picked CM has no teams yet, the field starts in Input mode.
   Iter 36.7 — replaces the free-form text Input previously used for TLs.
   ---------------------------------- */
function TLTeamPicker({ value, onChange, teams, disabled, testId }) {
  const knownTeam = teams.includes(value);
  const [mode, setMode] = useState(() => (teams.length === 0 || (!knownTeam && !!value)) ? "new" : "pick");
  // If the parent list changes and our current value is now unknown, drop
  // into "new" mode so the user sees it as free-form.
  useEffect(() => {
    if (value && !teams.includes(value) && mode !== "new") setMode("new");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams.length, value]);
  if (disabled) {
    return (
      <div className="h-8 rounded-md border border-slate-200 bg-slate-50 text-slate-400 text-xs px-2 flex items-center">
        Pick CM first
      </div>
    );
  }
  if (mode === "new" || teams.length === 0) {
    return (
      <div className="flex items-center gap-1">
        <Input
          className="h-8 flex-1"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. Team Batman"
          data-testid={testId}
        />
        {teams.length > 0 && (
          <button
            type="button"
            onClick={() => { setMode("pick"); onChange(""); }}
            className="text-[10px] text-purple-600 hover:text-purple-700 whitespace-nowrap px-1"
            title="Pick an existing team instead"
          >
            Pick existing
          </button>
        )}
      </div>
    );
  }
  return (
    <Select
      value={value || ""}
      onValueChange={(v) => { if (v === "__new__") { setMode("new"); onChange(""); return; } onChange(v); }}
    >
      <SelectTrigger className="h-8" data-testid={testId}><SelectValue placeholder="Pick team" /></SelectTrigger>
      <SelectContent>
        {teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        <div className="border-t border-slate-100 my-1" />
        <SelectItem value="__new__" className="text-purple-700 font-medium">+ New team…</SelectItem>
      </SelectContent>
    </Select>
  );
}

